<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';

const props = defineProps<{
  title?: string;
  content?: string;
  currentStep?: number;
  steps?: number;
  nextLabel?: string;
  hideRemindLater?: boolean;
  hideClose?: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits(['skip', 'remindLater', 'dontShow', 'nextTooltip']);

const { mobile } = useDisplay();
const { t } = useI18n();

const hasSteps = computed(() => props.steps && props.steps > 1);
</script>
<template>
  <div v-if="mobile" class="custom-overlay"></div>
  <VCard
    sentry-unmask
    :class="['rich-tooltip elevation-1', { 'rich-tooltip--mobile': mobile }]"
    role="dialog"
    color="surface-bright"
    data-test-id="richTooltip"
    aria-labelledby="richTooltipTitle"
    aria-describedby="richTooltipContent"
  >
    <CloseBtn
      v-if="(!mobile || (mobile && !hasSteps)) && !props.hideClose"
      icon
      color="on-surface"
      class="close-btn"
      data-test-id="richTooltip-close"
      @click="emit('skip')"
    >
      <VIcon>mdi-close</VIcon>
    </CloseBtn>
    <div class="rich-tooltip__wrapper">
      <slot name="image" v-if="!mobile || (mobile && !hasSteps)"></slot>
      <VCardActions v-if="mobile && hasSteps" class="rich-tooltip__actions">
        <TextBtn
          v-if="props.currentStep === 1"
          data-test-id="richTooltip-skip"
          @click="emit('skip')"
        >
          {{ t('richTooltip.actions.skip') }}
        </TextBtn>
        <TextBtn v-else data-test-id="richTooltip-dontShowAgain" @click="emit('dontShow')">
          {{ t('richTooltip.actions.dontShowAgain') }}
        </TextBtn>
        <TextBtn data-test-id="richTooltip-remindLater" @click="emit('remindLater')">
          {{ t('richTooltip.actions.remindMeLater') }}
        </TextBtn>
      </VCardActions>
      <VCardText class="rich-tooltip__content">
        <div class="rich-tooltip__text">
          <slot name="title">
            <VCardTitle
              id="richTooltipTitle"
              :class="['rich-tooltip__title', { 'is-centered': hasSteps && mobile }]"
              v-if="props.title"
            >
              {{ props.title }}
            </VCardTitle>
          </slot>
          <slot name="content">
            <p class="rich-tooltip__subtitle text-body-lg" id="richTooltipContent">
              {{ props.content }}
            </p>
          </slot>
        </div>
      </VCardText>
      <div class="rich-tooltip__image" v-if="mobile && hasSteps">
        <slot name="image"></slot>
      </div>
      <div class="next-btn-container" v-if="mobile">
        <TextBtn
          v-if="!hasSteps && !props.hideRemindLater"
          data-test-id="richTooltip-remindLater"
          @click="emit('remindLater')"
        >
          {{ t('richTooltip.actions.remindMeLater') }}
        </TextBtn>
        <FilledBtn
          v-if="!hasSteps || (props.currentStep && props.currentStep === props.steps)"
          data-test-id="richTooltip-nextTooltip"
          @click="emit('nextTooltip')"
          :loading="props.isLoading"
        >
          {{ props.nextLabel || t('richTooltip.actions.next') }}
        </FilledBtn>
        <CloseBtn
          v-if="hasSteps && props.steps !== props.currentStep"
          icon
          color="on-surface"
          data-test-id="richTooltip-nextTooltip"
          @click="emit('nextTooltip')"
          :loading="props.isLoading"
        >
          <VIcon>mdi-arrow-right</VIcon>
        </CloseBtn>
      </div>

      <div class="rich-tooltip__dots" v-if="hasSteps">
        <VIcon
          v-for="step in props.steps"
          :key="step"
          :color="step === props.currentStep ? 'primary' : 'outline'"
          size="10px"
        >
          mdi-circle
        </VIcon>
      </div>
      <VCardActions class="rich-tooltip__actions">
        <TextBtn
          v-if="!mobile && hasSteps && props.currentStep && props.currentStep > 1"
          data-test-id="richTooltip-dontShowAgain"
          @click="emit('dontShow')"
        >
          {{ t('richTooltip.actions.dontShowAgain') }}
        </TextBtn>
        <TextBtn
          v-if="
            (!mobile && !hasSteps && !props.hideRemindLater) || (!mobile && props.currentStep === 1)
          "
          data-test-id="richTooltip-remindLater"
          @click="emit('remindLater')"
        >
          {{ t('richTooltip.actions.remindMeLater') }}
        </TextBtn>
        <FilledBtn
          v-if="!mobile"
          data-test-id="richTooltip-nextTooltip"
          @click="emit('nextTooltip')"
          :loading="props.isLoading"
        >
          {{ props.nextLabel || t('richTooltip.actions.next') }}
        </FilledBtn>
      </VCardActions>
    </div>
  </VCard>
</template>
<style lang="scss" scoped>
.rich-tooltip {
  position: fixed;
  top: 5.25rem;
  right: 1rem;
  width: 31.5625rem;
  max-width: calc(100vw - (16rem + 2rem));
  z-index: 9999;

  &__wrapper {
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  &__content {
    padding: 0;
  }

  &__text {
    // padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__title {
    padding: 0;

    &.is-centered {
      text-align: center;
    }
  }

  &__subtitle {
    padding: 0;
  }

  &__dots {
    margin: 0.5rem 0;
    padding: 1rem 0;
    display: flex;
    gap: 0.1875rem;
    justify-content: center;
  }

  &__actions {
    width: 100%;
    justify-content: flex-end;
    padding: 0;
    min-height: unset;
    flex-wrap: wrap;

    &:has(button:nth-child(2)) {
      justify-content: space-between;
    }
  }

  &__image {
    display: flex;
    justify-content: center;
  }

  &--mobile {
    top: unset;
    right: 0;
    left: 0;
    width: 100%;
    max-width: unset;
    bottom: 0;
    padding: 1rem !important;
    max-height: calc(100vh - 3.75rem);
    overflow: auto;

    .rich-tooltip {
      &__wrapper {
        flex-direction: column;
      }

      // stylelint-disable-next-line no-descending-specificity
      &__actions {
        flex-wrap: wrap;
        align-items: center;
        padding: 0;
      }

      &__image {
        margin: 1rem 0;
      }
    }
  }
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.next-btn-container {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0.5rem 0;
  gap: 1rem;
}

.custom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.3;
  z-index: 1006;
}
</style>
